/**
 * Enter Tasks orchestrator hook.
 *
 * Bridges the gateway query/mutation (`use-gateway-queries`) with the UI store
 * (`enter-tasks-store`): exposes the filtered task list, the integration state,
 * and an optimistic `closeTask` action for the sidebar view to consume.
 */

import { useCallback } from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';
import type { EnterTask } from '@ctrl-enter/types';
import {
  useCloseTaskMutation,
  useGetIntegrationsQuery,
  useTaskCommentMutation,
  useTasksQuery,
} from './use-gateway-queries';
import { useSelectedEnterAccountId } from '../stores/enter-account-store';
import {
  useClearTaskClosing,
  useMarkTaskClosing,
  useSetTasksScope,
  useTasksClosingIds,
  useTasksFilter,
  useTasksScope,
} from '../stores/enter-tasks-store';

const logger = createLogger('client:hook:use-enter-tasks');

export type IntegrationState = 'connected' | 'none';

const SEVERITY_RANK: Record<EnterTask['severity'], number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

// Most urgent first, then earliest due date; tasks without a due date sort last.
function compareByPriorityThenDue(a: EnterTask, b: EnterTask): number {
  const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  if (bySeverity !== 0) return bySeverity;
  if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
  if (a.dueDate) return -1;
  if (b.dueDate) return 1;
  return 0;
}

export function useEnterTasks(enabled = true) {
  const scope = useTasksScope();
  const setScope = useSetTasksScope();
  // The practice picked in the composer. Tasks are read for THAT practice only
  // — an org with several connected Enter accounts used to get the union, so a
  // biller at one clinic saw every other clinic's worklist and counts.
  const accountId = useSelectedEnterAccountId();
  // With no practice chosen there is nothing to scope by, and the gateway falls
  // back to every connected account. For a single-practice org that union IS
  // the practice, so it stays correct; for an org with several it reproduces
  // the exact bug, so hold the request and ask the user to pick instead of
  // showing a merged worklist.
  const integrationsQuery = useGetIntegrationsQuery();
  const connectedCount =
    integrationsQuery.data?.status === 200
      ? (integrationsQuery.data.body.integrations?.length ?? 0)
      : 0;
  const needsAccount = !accountId && connectedCount > 1;
  const query = useTasksQuery(scope, enabled && !needsAccount, accountId);
  const filter = useTasksFilter();
  const closingIds = useTasksClosingIds();
  const markClosing = useMarkTaskClosing();
  const clearClosing = useClearTaskClosing();
  const closeMutation = useCloseTaskMutation();
  const commentMutation = useTaskCommentMutation();

  const body = query.data?.status === 200 ? query.data.body : undefined;
  const allTasks: EnterTask[] = body?.tasks ?? [];
  // `undefined` until a 200 lands — lets the view tell a genuine 'none' (no Enter
  // integration) apart from a failed request (404/401/500), which must NOT be
  // rendered as "not connected".
  const integration: IntegrationState | undefined = body?.integration;
  const isError = query.isError || (query.data != null && query.data.status !== 200);
  const filtered = filter === 'all' ? allTasks : allTasks.filter(t => t.severity === filter);
  // Sort a COPY so we never mutate the query cache's array in place.
  const tasks = filtered.slice().sort(compareByPriorityThenDue);

  const closeTask = useCallback(
    async (task: EnterTask, note?: string): Promise<boolean> => {
      markClosing(task.id);
      try {
        await closeMutation.mutateAsync({
          params: { id: task.id },
          body: { accountId: task.accountId, ...(note ? { note } : {}) },
        });
        return true;
      } catch (err) {
        // On failure the list isn't invalidated, so the task stays visible —
        // the row surfaces the error inline. (Toast polish is a later pass.)
        logger.error('[use-enter-tasks] closeTask failed', {
          taskId: task.id,
          error: err instanceof Error ? err.message : 'unknown',
        });
        return false;
      } finally {
        clearClosing(task.id);
      }
    },
    [closeMutation, markClosing, clearClosing]
  );

  /**
   * Post a comment on a task. Also how the Resolve actions reach Enter — the
   * body may be a sentinel string its backend matches on, so it is sent
   * verbatim and never trimmed here.
   */
  const postComment = useCallback(
    async (task: EnterTask, commentBody: string): Promise<boolean> => {
      try {
        const res = await commentMutation.mutateAsync({
          params: { id: task.id },
          body: { accountId: task.accountId, body: commentBody },
        });
        // ts-rest resolves non-2xx as a VALUE, not a throw — treating any
        // settled promise as success would report a failed resolve as done.
        return res.status === 200;
      } catch (err) {
        logger.error('[use-enter-tasks] postComment failed', {
          taskId: task.id,
          error: err instanceof Error ? err.message : 'unknown',
        });
        return false;
      }
    },
    [commentMutation]
  );

  return {
    tasks,
    allTaskCount: allTasks.length,
    integration,
    needsAccount,
    // `query.isLoading` stays true forever while the query is disabled, so a
    // held request would render as a permanent spinner instead of the prompt.
    isLoading: query.isLoading && !needsAccount,
    isError,
    refetch: query.refetch,
    closingIds,
    closeTask,
    postComment,
    scope,
    setScope,
  };
}
